import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Search, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface NoResultsProps {
    category?: string;
    searchTerm?: string;
}

const NoResults: React.FC<NoResultsProps> = ({ category, searchTerm }) => {
    const navigate = useNavigate();
    const { t } = useTranslation('marketplace');

    const context = category || (searchTerm ? `'${searchTerm}'` : t('noResults.searchContext'));

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20 px-6 bg-white dark:bg-gray-900 rounded-[3rem] border border-gray-100 dark:border-gray-800 flex flex-col items-center justify-center shadow-premium overflow-hidden relative"
        >
            {/* Liquid Glass Animation Container */}
            <div className="relative group mb-12">
                {/* Outer Glow */}
                <div className="absolute inset-0 bg-brand-accent/10 rounded-full blur-3xl group-hover:bg-brand-accent/20 transition-all scale-150"></div>

                <motion.div
                    className="relative w-48 h-48 bg-white dark:bg-gray-800 rounded-[3rem] shadow-2xl border border-gray-100 dark:border-gray-700 flex items-center justify-center overflow-hidden"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.8, ease: "backOut" }}
                >
                    {/* Floating Search Icon */}
                    <motion.div
                        animate={{
                            y: [0, -10, 0],
                            rotate: [0, 5, -5, 0]
                        }}
                        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                        className="relative z-10"
                    >
                        <Search size={80} className="text-gray-200 dark:text-gray-700" strokeWidth={1} />

                        {/* Sparkle Effects */}
                        <motion.div
                            className="absolute -top-2 -right-2 text-brand-accent"
                            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        >
                            <Sparkles size={24} fill="currentColor" />
                        </motion.div>
                    </motion.div>

                    {/* Ambient Dust / Bubbles */}
                    {[...Array(5)].map((_, i) => (
                        <motion.div
                            key={i}
                            className="absolute w-1.5 h-1.5 bg-brand-accent/30 rounded-full"
                            animate={{
                                y: [-20, -120],
                                x: [0, (i - 2) * 40],
                                opacity: [0, 0.6, 0],
                                scale: [0, 1, 0]
                            }}
                            transition={{
                                duration: 3,
                                delay: i * 0.7,
                                repeat: Infinity,
                                ease: "circOut"
                            }}
                            style={{ bottom: '20%' }}
                        />
                    ))}

                    {/* Inner Pulse */}
                    <motion.div
                        className="absolute inset-0 flex items-center justify-center pointer-events-none"
                        animate={{
                            scale: [0.8, 1.3, 0.8],
                            opacity: [0.1, 0.3, 0.1]
                        }}
                        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    >
                        <div className="w-32 h-32 bg-brand-accent/10 rounded-full blur-2xl" />
                    </motion.div>
                </motion.div>
            </div>

            <div className="relative z-10 space-y-4 max-w-md mx-auto">
                <h3 className="text-3xl font-black text-gray-950 dark:text-white tracking-tighter italic uppercase">
                    {t('noResults.title')}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 font-medium leading-relaxed text-lg px-4">
                    {t('noResults.emptyState', { context })}
                </p>

                <motion.button
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/sell')}
                    className="mt-8 px-12 py-5 bg-gray-950 dark:bg-white text-white dark:text-gray-950 rounded-2xl font-black text-[10px] uppercase tracking-[0.25em] shadow-[0_20px_40px_rgba(0,0,0,0.2)] dark:shadow-[0_20px_40px_rgba(255,255,255,0.05)] hover:bg-black dark:hover:bg-gray-100 transition-all flex items-center gap-3 mx-auto border border-white/10"
                >
                    <ShoppingBag size={18} strokeWidth={2.5} />
                    <span>{t('noResults.startSelling')}</span>
                </motion.button>
            </div>

            {/* Background Texture Blur */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-accent/5 blur-[120px] rounded-full pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-primary/5 blur-[120px] rounded-full pointer-events-none" />
        </motion.div>
    );
};

export default NoResults;
